import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Package, Eye, EyeOff, CheckCircle, AlertCircle,
  Building2, Mail, MapPin, Briefcase, ChevronDown,
  Paperclip, Upload, X, Check, FileText, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import { PhoneInput } from '../components/PhoneInput';
import { UN_COUNTRIES_EN } from '../countries';
import FieldError from '../components/FieldError';
import { isValidEmail, isValidPhoneDigits } from '../lib/validation';
import { checkPasswordRules } from '../lib/passwordPolicy';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PasswordRule = ({ ok, label }) => (
  <div className={`flex items-center gap-1.5 text-xs ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>
    {ok ? <CheckCircle className="h-3 w-3" /> : <div className="h-3 w-3 rounded-full border border-gray-300" />}
    {label}
  </div>
);

const StepIndicator = ({ currentStep, totalSteps, stepTitles }) => (
  <div className="flex items-center justify-center gap-0 mb-8">
    {Array.from({ length: totalSteps }, (_, i) => {
      const stepNum = i + 1;
      const isDone = stepNum < currentStep;
      const isActive = stepNum === currentStep;
      return (
        <React.Fragment key={stepNum}>
          <div className="flex flex-col items-center">
            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
              isDone ? 'bg-amber-500 text-white shadow-md' :
              isActive ? 'bg-white text-amber-600 shadow-lg ring-2 ring-amber-400' :
              'bg-amber-100 text-amber-400 border border-amber-200'
            }`}>
              {isDone ? <Check className="h-4 w-4" /> : stepNum}
            </div>
            <span className={`mt-1.5 text-xs font-medium whitespace-nowrap ${
              isActive ? 'text-[#0A3A5A]' : isDone ? 'text-amber-700' : 'text-[#5A6B7D]'
            }`}>{stepTitles[i]}</span>
          </div>
          {i < totalSteps - 1 && (
            <div className={`h-0.5 w-10 sm:w-16 mx-1 mt-[-18px] transition-all duration-300 ${
              stepNum < currentStep ? 'bg-amber-400' : 'bg-amber-200'
            }`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

function SummaryRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-gray-500 min-w-[80px]">{label}:</span>
      <span className="font-semibold text-gray-800 truncate">{value}</span>
    </div>
  );
}

export default function SupplierRegister() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = language === 'tr';
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    supplier_category_id: '', supplier_category_name: '',
    supplier_sub_category_id: '', supplier_sub_category_name: '',
    full_name: '', company: '', title: '',
    phone_code: '+90', phone_number: '',
    country: '', city: '',
    email: '', password: '', confirm_password: '',
    wantsAttachment: false,
  });
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passTouched, setPassTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const passwordRules = checkPasswordRules(form.password);
  const allRulesOk = Object.values(passwordRules).every(Boolean);
  const passwordsMatch = form.password === form.confirm_password;
  const emailValid = !form.email || isValidEmail(form.email);
  const phoneValid = !form.phone_number || isValidPhoneDigits(form.phone_number, { min: 7, max: 15 });
  const [fieldErrors, setFieldErrors] = useState({});
  const clearFieldError = (k) => setFieldErrors(prev => { if (!prev[k]) return prev; const n = {...prev}; delete n[k]; return n; });
  const activeSubcategories = categories.find(c => c.id === form.supplier_category_id)?.subcategories || [];

  const stepTitles = tr
    ? ['Kategori', 'Firma Bilgileri', 'Hesap', 'Belgeler']
    : ['Category', 'Company Info', 'Account', 'Documents'];

  useEffect(() => {
    axios.get(`${API}/auth/public/supplier-categories`)
      .then(r => setCategories(r.data || []))
      .catch(() => setCategories([{ id: 'default', name: 'Stand Constructor', subcategories: [] }]));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCategoryChange = (e) => {
    const selected = categories.find(c => c.id === e.target.value);
    setForm(f => ({
      ...f,
      supplier_category_id: e.target.value,
      supplier_category_name: selected?.name || '',
      supplier_sub_category_id: '',
      supplier_sub_category_name: '',
    }));
  };

  const handleSubCategoryChange = (e) => {
    const cat = categories.find(c => c.id === form.supplier_category_id);
    const selected = (cat?.subcategories || []).find(s => s.id === e.target.value);
    setForm(f => ({
      ...f,
      supplier_sub_category_id: e.target.value,
      supplier_sub_category_name: selected?.name || '',
    }));
  };

  const validateStep = (s) => {
    setError('');
    const errs = {};
    const reqMsg = tr ? 'Bu alan zorunludur.' : 'This field is required.';
    if (s === 1) {
      if (!form.supplier_category_id) errs.supplier_category_id = tr ? 'Tedarikçi kategorisi seçiniz.' : 'Please select a supplier category.';
      if (activeSubcategories.length > 0 && !form.supplier_sub_category_id) errs.supplier_sub_category_id = tr ? 'Alt kategori seçiniz.' : 'Please select a sub-category.';
    }
    if (s === 2) {
      if (!form.company.trim())   errs.company = reqMsg;
      if (!form.full_name.trim()) errs.full_name = reqMsg;
      if (form.phone_number && !phoneValid) { errs.phone_number = tr ? 'Telefon 7-15 rakam içermelidir.' : 'Phone must be 7-15 digits.'; setPhoneTouched(true); }
    }
    if (s === 3) {
      if (!form.email.trim()) { errs.email = reqMsg; setEmailTouched(true); }
      else if (!isValidEmail(form.email)) { errs.email = tr ? 'Geçerli bir e-posta giriniz.' : 'Please enter a valid email.'; setEmailTouched(true); }
      if (!form.password) errs.password = reqMsg;
      else if (!allRulesOk) { errs.password = tr ? 'Şifre kurallarını karşılamıyor.' : 'Password does not meet requirements.'; setPassTouched(true); }
      if (!form.confirm_password) errs.confirm_password = reqMsg;
      else if (!passwordsMatch) { errs.confirm_password = tr ? 'Şifreler uyuşmuyor.' : 'Passwords do not match.'; setConfirmTouched(true); }
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setError(tr ? 'Lütfen işaretli alanları kontrol ediniz.' : 'Please review the highlighted fields.');
      return false;
    }
    return true;
  };

  const nextStep = () => { if (!validateStep(step)) return; setStep(s => s + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const prevStep = () => { setError(''); setStep(s => s - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      const fd = new FormData();
      fd.append('full_name', form.full_name);
      fd.append('email', form.email);
      fd.append('company', form.company);
      fd.append('phone', form.phone_number ? `${form.phone_code} ${form.phone_number}` : '');
      fd.append('country', form.country);
      fd.append('city', form.city);
      fd.append('title', form.title);
      fd.append('supplier_category_id', form.supplier_category_id);
      fd.append('supplier_category_name', form.supplier_category_name);
      fd.append('supplier_sub_category_id', form.supplier_sub_category_id);
      fd.append('supplier_sub_category_name', form.supplier_sub_category_name);
      fd.append('password', form.password);
      fd.append('language', language);
      fd.append('is_individual', 'false');
      if (form.wantsAttachment && attachmentFiles.length > 0) {
        attachmentFiles.forEach(f => fd.append('attachments', f));
      }
      await axios.post(`${API}/auth/supplier-register`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || (tr ? 'Bir hata oluştu.' : 'An error occurred.'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#F7F5EF] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 shrink-0">
          <button
            onClick={() => navigate('/giris?tab=supplier&only=1')}
            className="flex items-center gap-2 text-[#0A3A5A]/70 hover:text-[#0A3A5A] transition-colors text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            {tr ? 'Giriş' : 'Login'}
          </button>
          <div className="text-[#0A3A5A] font-playfair text-2xl tracking-tight italic">SummitFlow</div>
          <LanguageToggle variant="default" />
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-xl border border-[rgba(10,58,90,0.08)] p-10 max-w-md w-full text-center">
            <div className="h-20 w-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="h-10 w-10 text-emerald-600" />
            </div>
            <h2 className="font-playfair text-2xl md:text-3xl text-[#0A3A5A] font-medium mb-3 tracking-tight">
              {tr ? 'Başvurunuz Alındı!' : 'Application Received!'}
            </h2>
            <div className="bg-[#F7F5EF] border border-[rgba(10,58,90,0.06)] rounded-2xl p-4 mb-5 text-left space-y-2">
              <SummaryRow label={tr ? 'Firma' : 'Company'} value={form.company} />
              <SummaryRow label={tr ? 'Kategori' : 'Category'} value={form.supplier_category_name} />
              <SummaryRow label={tr ? 'E-posta' : 'Email'} value={form.email} />
            </div>
            <p className="text-[#4A5568] text-sm mb-6 leading-relaxed">
              {tr
                ? 'Başvurunuz Ultra Admin tarafından incelenecektir. Onay sonrasında e-posta ile bilgilendirileceksiniz.'
                : 'Your application will be reviewed by Ultra Admin. You will be notified by email upon approval.'}
            </p>
            <Button data-testid="back-to-login-btn" onClick={() => navigate('/giris?tab=supplier&only=1')} className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8">
              {tr ? 'Giriş Sayfasına Dön' : 'Back to Login'}
            </Button>
          </div>
        </div>
        <div className="py-5 text-center text-[11px] text-[#5A6B7D]">© 2026 SummitFlow</div>
      </div>
    );
  }

  const inputCls = "rounded-xl border-gray-200 pl-10 focus:ring-amber-400";

  return (
    <div className="min-h-screen bg-[#F7F5EF] flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 shrink-0">
        <button
          onClick={() => step > 1 ? prevStep() : navigate('/giris?tab=supplier&only=1')}
          className="flex items-center gap-2 text-[#0A3A5A]/70 hover:text-[#0A3A5A] transition-colors text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          {step > 1 ? (tr ? 'Geri' : 'Back') : (tr ? 'Giriş' : 'Login')}
        </button>
        <div className="text-[#0A3A5A] font-playfair text-2xl tracking-tight italic">SummitFlow</div>
        <LanguageToggle variant="default" />
      </div>

      <div className="flex-1 flex items-start justify-center px-4 pb-10 pt-2">
        <div className="w-full max-w-lg">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-amber-100 border border-amber-200 mb-3">
              <Package className="h-7 w-7 text-amber-600" />
            </div>
            <h1 className="font-playfair text-3xl md:text-4xl text-[#0A3A5A] font-medium mb-1 tracking-tight">
              {tr ? 'Tedarikçi Başvurusu' : 'Supplier Application'}
            </h1>
            <p className="text-[#4A5568] text-sm">
              {tr ? 'SummitFlow tedarikçi platformuna başvurun' : 'Apply to the SummitFlow supplier platform'}
            </p>
          </div>

          <StepIndicator currentStep={step} totalSteps={4} stepTitles={stepTitles} />

          <div className="bg-white rounded-3xl shadow-xl border border-[rgba(10,58,90,0.08)] overflow-hidden">
            <div className="p-7 space-y-5">

              {/* ─── STEP 1: Category ─── */}
              {step === 1 && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-8 w-8 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Briefcase className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <h2 className="font-bold text-gray-800 text-base">{tr ? 'Tedarikçi Kategorisi' : 'Supplier Category'}</h2>
                      <p className="text-xs text-gray-400">{tr ? 'Uzmanlık alanınızı seçin.' : 'Select your area of expertise.'}</p>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold text-gray-700">
                      {tr ? 'Kategori' : 'Category'} <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-500 pointer-events-none" />
                      <select
                        data-testid="supplier-category-select"
                        value={form.supplier_category_id}
                        onChange={(e) => { handleCategoryChange(e); clearFieldError('supplier_category_id'); }}
                        className={`w-full pl-10 pr-8 py-2.5 border rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300 appearance-none text-gray-700 ${fieldErrors.supplier_category_id ? 'border-red-400' : 'border-gray-200'}`}
                      >
                        <option value="">{tr ? '-- Kategori Seçin --' : '-- Select Category --'}</option>
                        {categories.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                    <FieldError msg={fieldErrors.supplier_category_id} />
                  </div>

                  {activeSubcategories.length > 0 && (
                    <div className="space-y-1.5 animate-in fade-in duration-200">
                      <Label className="text-sm font-semibold text-gray-700">
                        {tr ? 'Alt Kategori' : 'Sub-Category'} <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <ChevronDown className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-400 pointer-events-none" />
                        <select
                          data-testid="supplier-sub-category-select"
                          value={form.supplier_sub_category_id}
                          onChange={(e) => { handleSubCategoryChange(e); clearFieldError('supplier_sub_category_id'); }}
                          className={`w-full pl-10 pr-8 py-2.5 border bg-amber-50 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 appearance-none text-gray-700 ${fieldErrors.supplier_sub_category_id ? 'border-red-400' : 'border-amber-200'}`}
                        >
                          <option value="">{tr ? '-- Alt Kategori --' : '-- Sub-Category --'}</option>
                          {activeSubcategories.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      </div>
                      <FieldError msg={fieldErrors.supplier_sub_category_id} />
                    </div>
                  )}
                </>
              )}

              {/* ─── STEP 2: Company Info ─── */}
              {step === 2 && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-8 w-8 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Building2 className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <h2 className="font-bold text-gray-800 text-base">{tr ? 'Firma Bilgileri' : 'Company Info'}</h2>
                      <p className="text-xs text-gray-400">{tr ? 'Firma ve iletişim bilgilerinizi girin.' : 'Enter your company and contact details.'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Firma Adı' : 'Company Name'} <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input data-testid="company-input" value={form.company} onChange={e => { set('company', e.target.value); clearFieldError('company'); }}
                          className={`${inputCls} ${fieldErrors.company ? 'border-red-400' : ''}`} placeholder={tr ? 'ABC Lojistik A.Ş.' : 'ABC Logistics Inc.'} />
                      </div>
                      <FieldError msg={fieldErrors.company} />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Ad Soyad' : 'Full Name'} <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input data-testid="fullname-input" value={form.full_name} onChange={e => { set('full_name', e.target.value); clearFieldError('full_name'); }}
                          className={`${inputCls} ${fieldErrors.full_name ? 'border-red-400' : ''}`} placeholder={tr ? 'Ahmet Yılmaz' : 'John Doe'} />
                      </div>
                      <FieldError msg={fieldErrors.full_name} />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Görev / Unvan' : 'Title'}</Label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input value={form.title} onChange={e => set('title', e.target.value)} className={inputCls} placeholder={tr ? 'Satış Müdürü' : 'Sales Manager'} />
                      </div>
                    </div>

                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Telefon' : 'Phone'}</Label>
                      <PhoneInput
                        codeValue={form.phone_code}
                        numberValue={form.phone_number}
                        onCodeChange={v => set('phone_code', v)}
                        onNumberChange={v => { set('phone_number', v); if (!phoneTouched && v) setPhoneTouched(true); }}
                      />
                      {phoneTouched && form.phone_number && !phoneValid && (
                        <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                          <AlertCircle className="h-3 w-3" />
                          {tr ? 'Telefon 7–15 rakam içermelidir (harf/özel karakter kabul edilmez)' : 'Phone must be 7–15 digits (no letters/symbols)'}
                        </p>
                      )}
                    </div>

                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Ülke' : 'Country'}</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
                        <select
                          value={form.country}
                          onChange={e => set('country', e.target.value)}
                          className="w-full pl-10 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300 appearance-none text-gray-700"
                        >
                          <option value="">{tr ? '-- Ülke Seçin --' : '-- Select Country --'}</option>
                          {UN_COUNTRIES_EN.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      </div>
                    </div>

                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Şehir' : 'City'}</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input value={form.city} onChange={e => set('city', e.target.value)} className={inputCls} placeholder="İstanbul" />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ─── STEP 3: Account ─── */}
              {step === 3 && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-8 w-8 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Settings className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <h2 className="font-bold text-gray-800 text-base">{tr ? 'Hesap Bilgileri' : 'Account Setup'}</h2>
                      <p className="text-xs text-gray-400">{tr ? 'E-posta ve güvenli bir şifre belirleyin.' : 'Set up your login credentials.'}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'E-posta' : 'Email'} <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input
                          data-testid="email-input"
                          type="email"
                          value={form.email}
                          onChange={e => { set('email', e.target.value.trim()); clearFieldError('email'); }}
                          onBlur={() => setEmailTouched(true)}
                          className={`${inputCls} ${(fieldErrors.email || (emailTouched && form.email && !emailValid)) ? 'border-red-400' : ''}`}
                          placeholder="ornek@firma.com"
                          autoComplete="email"
                        />
                      </div>
                      <FieldError
                        msg={fieldErrors.email || (emailTouched && form.email && !emailValid
                          ? (tr ? 'Geçerli bir e-posta giriniz (örn. ad@firma.com)' : 'Please enter a valid email (e.g. name@company.com)')
                          : null)}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Şifre' : 'Password'} <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Input
                          data-testid="password-input"
                          type={showPass ? 'text' : 'password'}
                          value={form.password}
                          onChange={e => { set('password', e.target.value); clearFieldError('password'); }}
                          onBlur={() => setPassTouched(true)}
                          className={`rounded-xl pr-10 ${(fieldErrors.password || (passTouched && !allRulesOk && form.password)) ? 'border-red-400' : 'border-gray-200'}`}
                          placeholder="••••••••"
                        />
                        <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <FieldError msg={fieldErrors.password} />
                      {(passTouched || form.password.length > 0) && (
                        <div className="grid grid-cols-2 gap-1 pt-1">
                          <PasswordRule ok={passwordRules.length}    label={tr ? 'En az 8 karakter' : 'Min 8 chars'} />
                          <PasswordRule ok={passwordRules.uppercase} label={tr ? 'Büyük harf' : 'Uppercase'} />
                          <PasswordRule ok={passwordRules.lowercase} label={tr ? 'Küçük harf' : 'Lowercase'} />
                          <PasswordRule ok={passwordRules.number}    label={tr ? 'Rakam' : 'Number'} />
                          <PasswordRule ok={passwordRules.special}   label={tr ? 'Özel karakter' : 'Special char'} />
                        </div>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Şifre Tekrar' : 'Confirm Password'} <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Input
                          data-testid="confirm-password-input"
                          type={showConfirm ? 'text' : 'password'}
                          value={form.confirm_password}
                          onChange={e => { set('confirm_password', e.target.value); clearFieldError('confirm_password'); }}
                          onBlur={() => setConfirmTouched(true)}
                          className={`rounded-xl pr-10 ${(fieldErrors.confirm_password || (confirmTouched && form.confirm_password && !passwordsMatch)) ? 'border-red-400' : confirmTouched && passwordsMatch && form.confirm_password ? 'border-emerald-400' : 'border-gray-200'}`}
                          placeholder="••••••••"
                        />
                        <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <FieldError
                        msg={fieldErrors.confirm_password || (confirmTouched && form.confirm_password && !passwordsMatch
                          ? (tr ? 'Şifreler uyuşmuyor.' : 'Passwords do not match.')
                          : null)}
                      />
                      {confirmTouched && form.confirm_password && passwordsMatch && allRulesOk && (
                        <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                          <CheckCircle className="h-3 w-3" />
                          {tr ? 'Şifreler eşleşiyor.' : 'Passwords match.'}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* ─── STEP 4: Documents & Summary ─── */}
              {step === 4 && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-8 w-8 rounded-xl bg-amber-100 flex items-center justify-center">
                      <FileText className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <h2 className="font-bold text-gray-800 text-base">{tr ? 'Belgeler & Özet' : 'Documents & Review'}</h2>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-2">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">{tr ? 'Başvuru Özeti' : 'Summary'}</p>
                    <SummaryRow label={tr ? 'Kategori' : 'Category'} value={form.supplier_category_name} />
                    {form.supplier_sub_category_name && <SummaryRow label={tr ? 'Alt Kategori' : 'Sub-cat'} value={form.supplier_sub_category_name} />}
                    <SummaryRow label={tr ? 'Firma' : 'Company'} value={form.company} />
                    <SummaryRow label={tr ? 'Ad Soyad' : 'Name'} value={form.full_name} />
                    <SummaryRow label={tr ? 'E-posta' : 'Email'} value={form.email} />
                    {form.country && <SummaryRow label={tr ? 'Ülke' : 'Country'} value={form.country} />}
                  </div>

                  <label
                    data-testid="attachment-checkbox-label"
                    className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      form.wantsAttachment ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-amber-200'
                    }`}
                  >
                    <div className="relative mt-0.5 flex-shrink-0">
                      <input
                        data-testid="attachment-checkbox"
                        type="checkbox"
                        checked={form.wantsAttachment}
                        onChange={e => {
                          set('wantsAttachment', e.target.checked);
                          if (!e.target.checked) setAttachmentFiles([]);
                        }}
                        className="sr-only"
                      />
                      <div className={`h-5 w-5 rounded border-2 flex items-center justify-center transition-all ${
                        form.wantsAttachment ? 'bg-amber-500 border-amber-500' : 'border-gray-300 bg-white'
                      }`}>
                        {form.wantsAttachment && <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{tr ? 'Dosya Eklemek İster Misiniz?' : 'Attach files?'}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{tr ? 'Şirket sunumu, CV veya tanıtım dosyası. (Maks. 3)' : 'Company presentation, CV, or intro file. (Max 3)'}</p>
                    </div>
                  </label>

                  {form.wantsAttachment && (
                    <div className="space-y-2">
                      <input
                        data-testid="attachment-file-input"
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={e => {
                          const picked = e.target.files?.[0];
                          if (!picked) return;
                          setAttachmentFiles(prev => {
                            if (prev.find(f => f.name === picked.name) || prev.length >= 3) return prev;
                            return [...prev, picked];
                          });
                          e.target.value = '';
                        }}
                      />
                      {attachmentFiles.map((f, idx) => (
                        <div key={idx} className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
                          <Paperclip className="h-4 w-4 text-amber-500 flex-shrink-0" />
                          <span className="text-sm text-gray-700 flex-1 truncate">{f.name}</span>
                          <button type="button" onClick={() => setAttachmentFiles(prev => prev.filter((_, i) => i !== idx))} className="text-gray-400 hover:text-red-500">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {attachmentFiles.length < 3 && (
                        <button
                          data-testid="attachment-browse-btn"
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-amber-300 rounded-xl text-amber-600 hover:bg-amber-50 text-sm font-medium"
                        >
                          <Upload className="h-4 w-4" />
                          {attachmentFiles.length === 0
                            ? (tr ? 'Dosya Seç' : 'Browse File')
                            : (tr ? `+ Dosya Ekle (${attachmentFiles.length}/3)` : `+ Add File (${attachmentFiles.length}/3)`)}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 hover:border-amber-300 hover:text-amber-600 transition-all text-sm font-medium"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {tr ? 'Geri' : 'Back'}
                  </button>
                )}
                {step < 4 ? (
                  <Button
                    data-testid={`step-${step}-next-btn`}
                    type="button"
                    onClick={nextStep}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl py-5 font-semibold text-sm flex items-center justify-center gap-2"
                  >
                    {tr ? 'Devam Et' : 'Continue'}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    data-testid="supplier-register-submit"
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl py-5 font-semibold text-sm"
                  >
                    {loading
                      ? <span className="flex items-center justify-center gap-2">
                          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          {tr ? 'Gönderiliyor...' : 'Submitting...'}
                        </span>
                      : (tr ? 'Başvuruyu Gönder' : 'Submit Application')}
                  </Button>
                )}
              </div>

              {step === 1 && (
                <p className="text-center text-xs text-gray-400">
                  {tr ? 'Zaten hesabınız var mı?' : 'Already have an account?'}{' '}
                  <button type="button" onClick={() => navigate('/giris?tab=supplier')} className="text-amber-600 font-medium hover:underline">
                    {tr ? 'Giriş Yap' : 'Login'}
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="py-5 text-center text-[11px] text-[#5A6B7D]">© 2026 SummitFlow</div>
    </div>
  );
}
